/**
 * Shared Phaser UI kit for arcade games. Design space is 720 wide; height follows the device
 * (Scale.EXPAND). Use these helpers so every game feels like one studio.
 */
import Phaser from "phaser";
import { currentLang, pick } from "./lang";

export const W = 720;
export const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
/** Brand palette (same as Mozaik). Index 0..5 are the "tile colors". */
export const COLORS = [0xff595e, 0xffca3a, 0x8ac926, 0x1982c4, 0x6a4c93, 0xff924c, 0x2ec4b6, 0xf15bb5];
export const BG = 0x1b1f3a;
export const PANEL = 0x262b4d;
export const PANEL_2 = 0x30365f;
export const TEXT_DIM = "#8f96c2";

/** Design height every game layout assumes (phones are taller: logical height ≥ 1280). */
export const DESIGN_H = 1280;

/**
 * Keep the 720×1280 design visible and centered on any screen. Wider than 9:16 → center
 * horizontally; shorter than the design (desktop, landscape tablets) → zoom out to fit.
 * Phones are never zoomed. Pointer worldX/worldY and interactive objects follow the camera.
 */
export function centerCamera(scene: Phaser.Scene): void {
  const fix = () => {
    const cam = scene.cameras.main;
    const sw = scene.scale.width;
    const sh = scene.scale.height;
    const z = Math.min(1, sh / DESIGN_H, sw / W);
    cam.setZoom(z);
    if (z < 1) cam.centerOn(W / 2, sh / z / 2);
    else cam.setScroll(-Math.max(0, sw - W) / 2, 0);
  };
  fix();
  scene.scale.on("resize", fix);
  scene.events.once("shutdown", () => scene.scale.off("resize", fix));
}

export interface BackdropStyle {
  /** Wall colour at the top and bottom of the screen. */
  top: number;
  bottom: number;
  /** Soft light behind the play area (each game passes its accent). */
  light: number;
}

const hex = (c: number): string => `#${c.toString(16).padStart(6, "0")}`;
const rgba = (c: number, a: number): string => `rgba(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255},${a})`;

/**
 * Studio background behind a game screen: a vertical gradient (extended sideways on screens wider than the design), a soft light around `lightY` in the game's accent and faint wallpaper dots. Baked into one texture per size
 * and style; returns the objects (depth -1000) so a scene can tweak them.
 */
export function backdrop(scene: Phaser.Scene, style: Partial<BackdropStyle> = {}, lightY?: number): Phaser.GameObjects.GameObject[] {
  const s: BackdropStyle = { top: 0x252f57, bottom: 0x11162d, light: 0xffc480, ...style };
  const h = Math.ceil(scene.scale.height / Math.min(1, scene.cameras.main.zoom || 1));
  const cy = Math.round(lightY ?? h * 0.55);
  // Each full-screen layer costs a fill per frame: the wide gradient is only drawn where the screen is wider than the
  // 720-wide art (tablets, desktop); phones get the one textured layer.
  const parts: Phaser.GameObjects.GameObject[] = [];
  if (scene.scale.width > W + 1) {
    const wall = scene.add.graphics().setDepth(-1001);
    wall.fillGradientStyle(s.top, s.top, s.bottom, s.bottom, 1).fillRect(-W * 3, 0, W * 7, h);
    parts.push(wall);
  }
  const key = `kit-bg-${h}-${cy}-${s.top}-${s.bottom}-${s.light}`;
  if (!scene.textures.exists(key)) {
    const tex = scene.textures.createCanvas(key, W, h);
    if (tex) {
      const ctx = tex.getContext();
      const lin = ctx.createLinearGradient(0, 0, 0, h);
      lin.addColorStop(0, hex(s.top));
      lin.addColorStop(1, hex(s.bottom));
      ctx.fillStyle = lin;
      ctx.fillRect(0, 0, W, h);
      const glow = ctx.createRadialGradient(W / 2, cy, 40, W / 2, cy, h * 0.55);
      glow.addColorStop(0, rgba(s.light, 0.14));
      glow.addColorStop(1, rgba(s.light, 0));
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, h);
      ctx.fillStyle = "rgba(255,255,255,0.035)";
      for (let y = 12; y < h; y += 28)
        for (let x = (y / 28) % 2 ? 26 : 12; x < W; x += 28) {
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      tex.refresh();
    }
  }
  const art = scene.add.image(W / 2, 0, key).setOrigin(0.5, 0).setDepth(-1000);
  parts.push(art);
  return parts;
}

export function text(scene: Phaser.Scene, x: number, y: number, s: string, size = 28, color = "#ffffff", bold = true): Phaser.GameObjects.Text {
  return scene.add.text(x, y, s, { fontFamily: FONT, fontSize: `${size}px`, color, fontStyle: bold ? "bold" : "normal", align: "center" }).setOrigin(0.5);
}

/** Rounded button; returns its container. Taps stop propagation so they never hit the board. */
export function button(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  color: number,
  onClick: () => void,
  textColor = "#ffffff",
): Phaser.GameObjects.Container {
  const g = scene.add.graphics();
  g.fillStyle(color, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 16);
  const t = scene.add.text(0, 0, label, { fontFamily: FONT, fontSize: `${Math.round(h * 0.36)}px`, color: textColor, fontStyle: "bold" }).setOrigin(0.5);
  const c = scene.add.container(x, y, [g, t]).setSize(w, h).setInteractive({ useHandCursor: true });
  c.on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
    ev.stopPropagation();
    scene.tweens.add({ targets: c, scale: 0.94, duration: 60, yoyo: true, onComplete: onClick });
  });
  return c;
}

/**
 * Top bar: title on the left, a back link ("‹ Oyunlar" / "‹ Games" to the hub, or e.g. "‹ Harita" for games
 * with a level map), a value (moves/score) on the right.
 */
export function topBar(
  scene: Phaser.Scene,
  title: string,
  rightLabel: string,
  back?: { label: string; onClick: () => void },
): { title: Phaser.GameObjects.Text; value: Phaser.GameObjects.Text } {
  scene.add.rectangle(W / 2, 0, W * 3, 150, 0x12152b).setOrigin(0.5, 0);
  const t = scene.add.text(36, 44, title, { fontFamily: FONT, fontSize: "34px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0, 0.5);
  scene.add
    .text(36, 96, back?.label ?? pick(currentLang(), { tr: "‹ Oyunlar", en: "‹ Games" }), { fontFamily: FONT, fontSize: "22px", color: TEXT_DIM })
    .setOrigin(0, 0.5)
    .setInteractive({ useHandCursor: true })
    .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      if (back) back.onClick();
      else location.href = new URLSearchParams(location.search).has("shell") ? "/" : "https://enjoyment.app/?utm_source=arcade";
    });
  scene.add.text(W - 36, 30, rightLabel, { fontFamily: FONT, fontSize: "22px", color: TEXT_DIM }).setOrigin(1, 0.5);
  const v = scene.add.text(W - 36, 86, "", { fontFamily: FONT, fontSize: "60px", color: "#ffffff", fontStyle: "bold" }).setOrigin(1, 0.5);
  return { title: t, value: v };
}

export interface OverlayButton {
  label: string;
  color: number;
  onClick: () => void;
}

/** Modal end-of-level card. Returns the container; destroy it to close. */
export function overlay(scene: Phaser.Scene, title: string, sub: string, buttons: OverlayButton[]): Phaser.GameObjects.Container {
  const H = scene.scale.height;
  const c = scene.add.container(0, 0).setDepth(1000);
  const dim = scene.add.rectangle(W / 2, H / 2, W * 3, H, 0x000000, 0.6).setInteractive();
  const panel = scene.add.graphics();
  panel.fillStyle(PANEL, 1).fillRoundedRect(W / 2 - 300, H / 2 - 210, 600, 420, 24);
  panel.lineStyle(4, 0x3a4170, 1).strokeRoundedRect(W / 2 - 300, H / 2 - 210, 600, 420, 24);
  const tt = text(scene, W / 2, H / 2 - 130, title, 46);
  const st = text(scene, W / 2, H / 2 - 55, sub, 24, "#b8bee6", false).setWordWrapWidth(540);
  c.add([dim, panel, tt, st]);
  const bw = buttons.length > 1 ? 250 : 320;
  const total = buttons.length * bw + (buttons.length - 1) * 20;
  buttons.forEach((b, i) => c.add(button(scene, W / 2 - total / 2 + bw / 2 + i * (bw + 20), H / 2 + 110, bw, 84, b.label, b.color, b.onClick)));
  c.setScale(0.9).setAlpha(0);
  scene.tweens.add({ targets: c, scale: 1, alpha: 1, duration: 180, ease: "Back.Out" });
  return c;
}

/** Floating text (combo, +points). */
export function floatText(scene: Phaser.Scene, x: number, y: number, s: string, color = "#ffffff", size = 40): void {
  const t = scene.add.text(x, y, s, { fontFamily: FONT, fontSize: `${size}px`, color, fontStyle: "bold", stroke: "#1b1f3a", strokeThickness: 8 }).setOrigin(0.5).setDepth(500).setScale(0.5);
  scene.tweens.chain({
    targets: t,
    tweens: [
      { scale: 1.1, duration: 140, ease: "Back.Out" },
      { y: y - 90, alpha: 0, duration: 520, delay: 160, ease: "Quad.In", onComplete: () => t.destroy() },
    ],
  });
}

/** Confetti burst from the top (win). */
export function confetti(scene: Phaser.Scene): void {
  if (!scene.textures.exists("kit-spark")) {
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1).fillRoundedRect(0, 0, 14, 14, 4);
    g.generateTexture("kit-spark", 14, 14);
    g.destroy();
  }
  for (let i = 0; i < 36; i++) {
    const x = 60 + ((i * 97) % 600);
    const s = scene.add.image(x, -20, "kit-spark").setTint(COLORS[i % 6]!).setDepth(900);
    scene.tweens.add({ targets: s, y: scene.scale.height + 40, x: x + ((i % 5) - 2) * 40, angle: 360, duration: 1400 + (i % 7) * 120, delay: (i % 9) * 40, ease: "Quad.In", onComplete: () => s.destroy() });
  }
}

/** Web Share with clipboard fallback. Returns true if shared/copied. */
export async function share(textBody: string, url: string, title: string, file?: File | null): Promise<boolean> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    if (file && nav.share && nav.canShare?.({ files: [file] })) await nav.share({ files: [file], text: `${textBody}\n${url}`, title });
    else if (nav.share) await nav.share({ text: `${textBody}\n${url}`, title });
    else await navigator.clipboard.writeText(`${textBody}\n${url}`);
    return true;
  } catch {
    return false;
  }
}
